import { useState, useCallback } from 'react';
import { toast } from '@/lib/toast';
import type { WorkflowDocument } from '@/lib/services/correspondences/workflows/workflow-types';
import type { FileType, MatterFile } from '@/lib/services/files/types';

interface UploadFileOptions {
  displayName?: string;
  isCertifiedCopy?: boolean;
  matterId?: number;
  attachmentId?: number;
}

interface UseAttachmentsProps {
  uploadFile: (
    file: File,
    fileType: FileType,
    options?: UploadFileOptions
  ) => Promise<MatterFile | null | undefined>;
  refetchFiles: () => Promise<unknown>;
  matterId?: number;
}

export function useAttachments({
  uploadFile,
  refetchFiles,
  matterId: _matterId
}: UseAttachmentsProps) {
  const [pendingAttachments, setPendingAttachments] = useState<
    WorkflowDocument[]
  >([]);
  const [showAttachmentDialog, setShowAttachmentDialog] = useState(false);

  const handleUploadDocument = useCallback(
    async (
      files: Array<{ file: File; fileType: string; isCertifiedCopy: boolean }>
    ) => {
      try {
        const uploadedDocuments: WorkflowDocument[] = [];

        for (const { file, fileType: _fileType, isCertifiedCopy } of files) {
          try {
            const uploadedFile = await uploadFile(
              file,
              'additional-document' as FileType,
              {
                displayName: file.name,
                isCertifiedCopy
              }
            );

            if (uploadedFile) {
              const fileTypeString =
                typeof uploadedFile.fileType === 'string'
                  ? uploadedFile.fileType
                  : uploadedFile.fileType?.slug || 'additional-document';

              const workflowDoc: WorkflowDocument = {
                id: uploadedFile.id,
                originalFilename: uploadedFile.originalFilename || file.name,
                displayName: uploadedFile.displayName,
                createdAt: uploadedFile.createdAt,
                updatedAt: uploadedFile.updatedAt,
                status: uploadedFile.status || 'available',
                s3Key: uploadedFile.s3Key,
                imageS3Key: uploadedFile.imageS3Key || null,
                imageMimeType: uploadedFile.imageMimeType || null,
                mimeType: uploadedFile.mimeType || file.type,
                fileType: fileTypeString,
                extractedData: uploadedFile.extractedData || null,
                isCertifiedCopy:
                  uploadedFile.isCertifiedCopy || isCertifiedCopy,
                metadata: uploadedFile.metadata || null,
                analysisMetadata: uploadedFile.analysisMetadata || null,
                fileSizeKb:
                  uploadedFile.fileSizeKb || Math.ceil(file.size / 1024),
                deletedAt: uploadedFile.deletedAt || null
              };

              uploadedDocuments.push(workflowDoc);
            }

            toast.success(`Uploaded ${file.name}`);
            await refetchFiles();
          } catch (uploadError) {
            console.error(`Failed to upload ${file.name}:`, uploadError);
            toast.error(`Failed to upload ${file.name}`);
          }
        }

        if (uploadedDocuments.length > 0) {
          setPendingAttachments((prev) => [...prev, ...uploadedDocuments]);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload files');
      }
    },
    [uploadFile, refetchFiles]
  );

  const handleAddAttachments = useCallback((documents: WorkflowDocument[]) => {
    setPendingAttachments((prev) => [...prev, ...documents]);
    setShowAttachmentDialog(false);
  }, []);

  const handleRemoveAttachment = useCallback((documentId: number) => {
    setPendingAttachments((prev) => prev.filter((d) => d.id !== documentId));
  }, []);

  const handleAttachClick = useCallback(() => {
    setShowAttachmentDialog(true);
  }, []);

  const clearAttachments = useCallback(() => {
    setPendingAttachments([]);
  }, []);

  return {
    pendingAttachments,
    showAttachmentDialog,
    setShowAttachmentDialog,
    setPendingAttachments,
    handleUploadDocument,
    handleAddAttachments,
    handleRemoveAttachment,
    handleAttachClick,
    clearAttachments
  };
}
